import { ContactShadows, Grid } from '@react-three/drei'
import { DoubleSide } from 'three'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { AmbienteEstudio } from './AmbienteEstudio'
import { RECURSOS_QUALIDADE, type NivelQualidade } from './qualidadeGrafica'
import { ALCANCE_SOMBRA_MESA } from '../tabletop/dimensoesMesa'

const COR_FUNDO = '#0c1117'

/**
 * Parâmetros de renderização comuns às cenas de estúdio.
 *
 * O tone mapping fica no padrão do R3F (ACES) de propósito. AgX e Neutro (PBR
 * Neutral) foram medidos contra ele e descartados, porque a paleta do projeto
 * foi acertada de olho sob o ACES e a cor aqui é semântica:
 * - AgX dessatura: o vermelho da estrutura caiu de 0,71 para 0,54 de saturação
 *   (HSV) e passou a ler como salmão;
 * - Neutro preserva a saturação, mas o âmbar `#f59e0b` da triagem "amarelo" e
 *   da tinta da pista sai com matiz 33° em vez dos 43° de hoje, e o amarelo
 *   passa a ler como laranja, perto demais do vermelho.
 * Trocar o tone mapping exige reajustar as cores de triagem junto com os chips
 * da interface que usam a mesma cor, e não só a exposição.
 */
export const CONFIGURACAO_GL_ESTUDIO = {
  antialias: true,
  toneMappingExposure: 1.15,
} as const

/**
 * O grid fica 4 mm abaixo do piso, para não disputar profundidade com a sombra
 * de contato — que precisa ficar na origem, sob pena de laço de atualização.
 */
const POSICAO_GRADE: [number, number, number] = [0, -0.004, 0]

/**
 * A câmera nunca passa de 16 m do alvo (30 m no Table top), então a neblina só
 * começa depois disso: ela some com o horizonte e com o fim da pista, e não
 * deve esmaecer o veículo que está sendo estudado.
 */
const NEBLINA = { padrao: [26, 56], ampla: [44, 96] } as const
const GRADE = { padrao: { desvanece: 26, forca: 1.6 }, ampla: { desvanece: 44, forca: 1.6 } } as const

/**
 * Luz-chave com sombra.
 *
 * O volume de sombra acompanha o veículo em vez de ser fixo: uma câmera
 * ortográfica folgada demais desperdiça resolução e a sombra sai serrilhada.
 * O `bias` negativo existe porque as chapas da carroceria são `DoubleSide`,
 * e face dupla sem bias produz acne na própria superfície iluminada.
 *
 * A borda macia vem do `shadow-radius`, que o filtro PCF aplica em texels —
 * por isso o raio acompanha o tamanho do mapa em `RECURSOS_QUALIDADE`.
 */
function LuzChave({ perfil, qualidade, diorama }: { perfil: PerfilVeiculo; qualidade: NivelQualidade; diorama: boolean }) {
  // no diorama a sombra cobre o tabuleiro inteiro (24 m), não só um veículo;
  // o mapa dobra para a resolução por metro não cair demais
  const alcance = diorama ? ALCANCE_SOMBRA_MESA : perfil.comprimento * 0.9
  const { mapaSombra: mapaBase, raioSombra } = RECURSOS_QUALIDADE[qualidade]
  const mapaSombra = diorama ? mapaBase * 2 : mapaBase
  const distancia = diorama ? 6 : 1

  return (
    <directionalLight
      // O R3F não realoca o mapa de sombra quando `mapSize` muda depois do
      // primeiro quadro; remontar a luz é o que faz o novo tamanho valer.
      key={mapaSombra}
      position={[4 * distancia, 7 * distancia, 3 * distancia]}
      intensity={1.9}
      castShadow
      shadow-mapSize={[mapaSombra, mapaSombra]}
      shadow-radius={raioSombra}
      shadow-bias={-0.0009}
      shadow-normalBias={0.02}
      shadow-camera-left={-alcance}
      shadow-camera-right={alcance}
      shadow-camera-top={alcance}
      shadow-camera-bottom={-alcance}
      shadow-camera-near={0.5}
      shadow-camera-far={24 * distancia}
    />
  )
}

interface Props {
  perfil: PerfilVeiculo
  qualidade: NivelQualidade
  /** Cena em que a câmera se afasta muito (Table top): neblina e grade começam mais longe. */
  ampla?: boolean
  /**
   * A cena traz o próprio tabuleiro de maquete (Table top com pista): sem
   * grade, e a sombra do sol cobrindo a mesa inteira.
   */
  diorama?: boolean
}

/**
 * Palco comum das cenas 3D: fundo, neblina, luzes, sombras e chão.
 *
 * Existe para que a maquete e a tela de projeção do Table top não mantenham,
 * cada uma, a sua cópia do estúdio — as duas já tinham divergido (a projeção
 * ficou sem sombra). Quem monta o `<Canvas>` passa `CONFIGURACAO_GL_ESTUDIO`
 * em `gl` e põe a cena como irmã deste componente.
 */
export function PalcoEstudio({ perfil, qualidade, ampla = false, diorama = false }: Props) {
  const [perto, longe] = NEBLINA[ampla ? 'ampla' : 'padrao']
  const grade = GRADE[ampla ? 'ampla' : 'padrao']

  return (
    <>
      <color attach="background" args={[COR_FUNDO]} />
      {/* Fundo liso escuro em todas as cenas, sem paisagem. A neblina tem a
          mesma cor: o que some nela some para o fundo, sem halo. */}
      <fog attach="fog" args={[COR_FUNDO, perto, longe]} />

      {/* O mapa de ambiente fornece todo o preenchimento. Manter a ambiente
          antiga em 1.15 lavaria a cena e anularia o ganho: o que dá volume é a
          diferença entre luz e sombra, não o nível geral. */}
      <ambientLight intensity={0.38} />
      <hemisphereLight args={['#f4e8df', '#111820', 0.35]} />
      <AmbienteEstudio perfil={perfil} />

      <LuzChave perfil={perfil} qualidade={qualidade} diorama={diorama} />
      <directionalLight position={[-5, 3, -4]} intensity={0.45} />

      {/* Sombra de contato: é o que assenta o veículo no piso. Sem ela o carro
          parecia flutuar sobre o grid em todas as cenas. */}
      {!diorama && <ContactShadows
        // Fica na origem, e quem desce é o grid. Deslocar a própria
        // ContactShadows põe o React em laço de atualização nas cenas que
        // redesenham a todo quadro — Table top e extração. A separação de 4 mm
        // contra o z-fighting sai igual movendo o outro lado.
        scale={perfil.comprimento * 1.6}
        // 512 já basta depois do borrão, e a sombra precisa acompanhar peça
        // que abre e veículo que se move — por isso não é assada uma vez só
        resolution={512}
        blur={3}
        opacity={0.6}
        far={perfil.tetoY + 0.6}
        color="#05080c"
        // sem escrever profundidade o grid continua visível através da parte
        // clara da sombra, em vez de ser recortado por um quadrado escuro
        depthWrite={false}
      />}

      {/* Linhas de meio em meio metro, com o metro cheio mais marcado: a grade
          continua servindo de régua. Desvanece a partir da origem (o veículo),
          não da câmera, para o chão não "acompanhar" quem orbita. */}
      {!diorama && <Grid
        position={POSICAO_GRADE}
        infiniteGrid
        fadeFrom={0}
        fadeDistance={grade.desvanece}
        fadeStrength={grade.forca}
        cellSize={0.5}
        cellThickness={0.6}
        cellColor="#242a31"
        sectionSize={1}
        sectionThickness={1}
        sectionColor="#3b434d"
        // o gridHelper antigo era visível dos dois lados; a extração deixa a
        // câmera passar para baixo do piso quando o veículo está tombado
        side={DoubleSide}
      />}
    </>
  )
}
